import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/app/components/ui/use-toast';
import {
  ACCEPT_VIDEO_MIMETYPE,
  MAX_VIDEO_DURATION_IN_SECONDS,
  MAX_VIDEO_FILE_SIZE,
  MIN_VIDEO_DURATION_IN_SECONDS,
  UPLOAD_SCENE,
  type UploadScene,
} from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { SourceVideo, VideoUploadFile, VideoUploadFileInfo } from '@/app/types';
import {
  formatFileSize,
  getVideoMetadataErrorCode,
  isVideoDurationWithinRange,
  VideoMetadataErrorCode,
} from '@/app/utils';
import { createUploadController, getVideoMetadata } from '@/app/utils/client';
import { useGlobalModal } from './use-global-modal';

type VideoType = 'video/mp4' | 'video/webm' | 'video/quicktime';

const VIDEO_TYPE_MAP: Record<string, string> = {
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/webm': 'WEBM',
};

interface Detector {
  type: VideoType;
  check: (view: Uint8Array) => boolean;
}

const detectors: Detector[] = [
  {
    type: 'video/mp4',
    // MP4: ftyp
    check: (buffer) => buffer[4] === 0x66 && buffer[5] === 0x74 && buffer[6] === 0x79 && buffer[7] === 0x70,
  },
  {
    type: 'video/quicktime',
    // MOV: ftypqt (QuickTime)
    check: (buffer) =>
      buffer[4] === 0x66 &&
      buffer[5] === 0x74 &&
      buffer[6] === 0x79 &&
      buffer[7] === 0x70 &&
      buffer[8] === 0x71 &&
      buffer[9] === 0x74,
  },
  {
    type: 'video/webm',
    // WebM: "1A 45 DF A3"
    check: (buffer) => buffer[0] === 0x1a && buffer[1] === 0x45 && buffer[2] === 0xdf && buffer[3] === 0xa3,
  },
];

function detectVideoTypeByBuffer(buffer: ArrayBuffer): VideoType | null {
  const uint8Array = new Uint8Array(buffer);

  for (const detector of detectors) {
    if (detector.check(uint8Array)) {
      return detector.type;
    }
  }

  return null;
}

const detectVideoType = async (file: File): Promise<VideoType | null> => {
  const buffer = await file.slice(0, 12).arrayBuffer();
  return detectVideoTypeByBuffer(buffer);
};

/**
 * 获取用户友好的视频格式名称
 */
export function getFriendlyVideoTypeName(mimeType: string): string {
  if (VIDEO_TYPE_MAP[mimeType]) {
    return VIDEO_TYPE_MAP[mimeType];
  }
  // 兜底处理：取 / 后的部分并转大写
  const parts = mimeType.split('/');
  const type = parts[1] || mimeType;
  return type.toUpperCase();
}

interface UseVideoUploadOptions {
  maxDuration?: number;
  minDuration?: number;
  maxSize?: number;
  scene?: UploadScene;
  /** 支持的视频格式，不传则使用默认的 ACCEPT_VIDEO_MIMETYPE */
  videoAccept?: string[];
  /** 是否截取视频首帧作为封面图 */
  captureCoverImage?: boolean;
  /** 是否将截取的封面图上传到服务器 */
  uploadCoverImage?: boolean;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
  emitUploadingInTempMode?: boolean; // 临时模式下是否仍然先发出 uploading 事件
  emitUploadingPreview?: boolean; // 视频解析出封面后，上传完成前再发一次带封面的 uploading 事件
  validateMetadata?: (metadata: {
    duration: number;
    aspectRatio: number;
    width: number;
    height: number;
  }) => string | null;
  /** 统一的上传状态变化回调（与图片上传保持一致） */
  onChange?: (file: VideoUploadFile, fileUploadInfo?: VideoUploadFileInfo) => void;
  /**
   * 视频本地元数据（时长 / 宽高）解析完毕后回调，早于实际上传完成。
   * 调用方可据此立即在 UI 上根据已知 duration 占位（如时间轴 loading 片段），
   * 而无须等到 onChange 抛出 `done` 才能显示。
   */
  onMetadataReady?: (info: { id: string; batchIndex?: number; durationSec: number; name?: string }) => void;
}

interface VideoUploadResult {
  uploading: boolean;
  selectVideo: () => void;
  uploadFile: (file: File, batchIndex?: number) => Promise<void>;
  cancelUpload: () => void;
}

/**
 * 通用的视频上传 Hook
 *
 * 提供视频文件选择、验证、上传的完整流程
 * 与 UI 解耦，可在任何需要视频上传的地方使用
 *
 */
export const useVideoUpload = ({
  maxDuration = MAX_VIDEO_DURATION_IN_SECONDS,
  minDuration = MIN_VIDEO_DURATION_IN_SECONDS,
  maxSize = MAX_VIDEO_FILE_SIZE,
  scene = UPLOAD_SCENE.VIDU,
  videoAccept = ACCEPT_VIDEO_MIMETYPE,
  captureCoverImage = false,
  uploadCoverImage = false,
  allowTempMode = false,
  emitUploadingInTempMode = true,
  emitUploadingPreview = false,
  validateMetadata,
  onChange,
  onMetadataReady,
}: UseVideoUploadOptions = {}): VideoUploadResult => {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const userInfo = useStore((state) => state.userInfo);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cancelUploadRef = useRef<(() => void) | null>(null);
  const maxSizeLabel = formatFileSize(maxSize)?.replace(' ', '') ?? `${Math.round(maxSize / 1024 / 1024)}MB`;

  /**
   * 显示错误提示
   */
  const showError = (msg: string) => {
    toast({
      type: 'error',
      message: msg,
    });
  };

  /**
   * 验证并上传视频文件
   */
  const handleFileChange = useCallback(
    async (file: File, batchIndex?: number) => {
      const fileUploadInfo: VideoUploadFileInfo = {
        resolution: '',
        video_type: '',
        is_success: false,
        fail_reason: '',
        upload_time: 0,
      };
      const id = uuidv4();
      const startUploadTime = performance.now();
      const isTempMode = allowTempMode && !userInfo;

      if (!isTempMode || emitUploadingInTempMode) {
        onChange?.({
          id,
          file: null,
          status: 'uploading',
          batchIndex,
        });
      }

      // 检查文件类型
      if (videoAccept.includes(file.type)) {
        const fileType = await detectVideoType(file);
        fileUploadInfo.video_type = fileType || '';
        if (!fileType || !videoAccept.includes(fileType)) {
          const errorMsg = t('sharedComponent.videoUploader.error.incorrectExtension');
          showError(errorMsg);
          fileUploadInfo.fail_reason = 'incorrectExtension';
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
              batchIndex,
            },
            fileUploadInfo,
          );
          return;
        }
      } else {
        const errorMsg = t('sharedComponent.videoUploader.error.invalidType', {
          accept: videoAccept.map((type) => getFriendlyVideoTypeName(type)).join(', '),
        });
        showError(errorMsg);
        fileUploadInfo.video_type = file.type;
        fileUploadInfo.fail_reason = 'invalidType';
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
            batchIndex,
          },
          fileUploadInfo,
        );
        return;
      }

      // 检查文件大小
      if (file.size > maxSize) {
        const errorMsg = t('sharedComponent.videoUploader.error.invalidSizeWithLimit', {
          limit: maxSizeLabel,
        });
        showError(errorMsg);
        fileUploadInfo.fail_reason = 'invalidSize';
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
            batchIndex,
          },
          fileUploadInfo,
        );
        return;
      }

      // 获取视频元数据（时长和宽高比）
      let objectUrl: string | null = null;
      try {
        // 创建 File 的 object URL 用于获取元数据
        objectUrl = URL.createObjectURL(file);
        const { duration, aspectRatio, width, height, cover_url, cover_blob } = await getVideoMetadata(objectUrl, {
          captureCoverImage,
          // 临时模式下也保留封面 Blob，便于登录后补传
          needCoverBlob: uploadCoverImage,
        });
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;

        fileUploadInfo.resolution = `${width}x${height}`;

        // 检查视频时长
        if (
          !isVideoDurationWithinRange({
            duration,
            minDuration,
            maxDuration,
          })
        ) {
          const errorMsg =
            maxDuration < 60
              ? t('sharedComponent.videoUploader.error.invalidDurationInSeconds', {
                  min: minDuration,
                  max: Math.round(maxDuration),
                })
              : t('sharedComponent.videoUploader.error.invalidDuration', {
                  min: minDuration,
                  max: Math.ceil(maxDuration / 60),
                });
          showError(errorMsg);
          fileUploadInfo.fail_reason = 'invalidDuration';
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
              batchIndex,
            },
            fileUploadInfo,
          );
          return;
        }

        const metadataValidationError = validateMetadata?.({
          duration,
          aspectRatio,
          width,
          height,
        });

        onMetadataReady?.({ id, batchIndex, durationSec: duration, name: file.name });

        if (metadataValidationError) {
          showError(metadataValidationError);
          fileUploadInfo.fail_reason = 'invalidMetadata';
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
              batchIndex,
            },
            fileUploadInfo,
          );
          return;
        }

        if (emitUploadingPreview && cover_url) {
          onChange?.({
            id,
            file: {
              id: `uploading-video-${id}`,
              tempId: id,
              src: '',
              preview: '',
              aspectRatio,
              duration,
              cover_url: '',
              cover_preview_url: cover_url,
              width,
              height,
            },
            status: 'uploading',
            batchIndex,
            width,
            height,
          });
        }

        if (isTempMode) {
          // 临时模式：直接返回成功，但不上传
          fileUploadInfo.is_success = false;
          fileUploadInfo.upload_time = performance.now() - startUploadTime;
          const tempVideoPreview = URL.createObjectURL(file);

          const video: SourceVideo = {
            id: `temp-video-${id}`,
            tempId: id,
            src: '', // 临时模式没有服务端 URI
            preview: tempVideoPreview,
            aspectRatio,
            duration,
            cover_url: '',
            cover_preview_url: cover_url,
            width,
            height,
            _tempFile: file,
            _tempCoverImage: cover_blob,
          };

          onChange?.(
            {
              id,
              file: video,
              status: 'done',
              batchIndex,
              width,
              height,
            },
            fileUploadInfo,
          );
          return;
        }

        setUploading(true);

        const videoURL = URL.createObjectURL(file);
        const { cancelUpload, uploadFile: uploadFileAction } = createUploadController();
        cancelUploadRef.current = cancelUpload;

        // 上传封面图
        const coverUploadPromise =
          uploadCoverImage && cover_blob
            ? uploadFileAction({
                file: cover_blob,
                scene,
              }).then((coverUploadResult) => {
                // 如果封面图审核失败，取消视频上传并且报错
                if (coverUploadResult.error?.reason === 'AuditSubmitIllegal') {
                  cancelUploadRef.current?.();

                  const auditId = coverUploadResult?.error?.metadata?.audit_id;
                  const message = t('create.createForm.upload.error.AuditSubmitIllegal');
                  openConfirmDialog({
                    title: t('popup.dialog.error.checkFailed.title'),
                    content: auditId ? `${message}\n(ID: ${auditId})` : message,
                    primaryButtonText: t('popup.dialog.error.confirm'),
                  });

                  fileUploadInfo.fail_reason = 'AuditSubmitIllegal';
                }
                return coverUploadResult;
              })
            : null;

        // 上传视频，将 duration 写入 metadata
        const videoUploadPromise = uploadFileAction({ file, scene, duration, width, height, videoAccept });
        const [coverUploadResult, videoUploadResult] = await Promise.all([coverUploadPromise, videoUploadPromise]);
        cancelUploadRef.current = null;

        if (videoUploadResult.cancelled || coverUploadResult?.error?.reason === 'AuditSubmitIllegal') {
          onChange?.({
            id,
            file: null,
            status: 'removed',
            batchIndex,
          });
          return;
        }

        // 检查视频上传错误
        if (videoUploadResult.error) {
          if (videoUploadResult.error?.reason === 'AuditSubmitIllegal') {
            const auditId = videoUploadResult.error?.metadata?.audit_id;
            const message = t('create.createForm.upload.error.AuditSubmitIllegal');
            openConfirmDialog({
              title: t('popup.dialog.error.checkFailed.title'),
              content: auditId ? `${message}\n(ID: ${auditId})` : message,
              primaryButtonText: t('popup.dialog.error.confirm'),
            });
          } else if (videoUploadResult.error) {
            showError(t('popup.toast.uploadingError'));
          }

          fileUploadInfo.fail_reason = videoUploadResult.error?.reason || '';
          onChange?.(
            {
              id,
              file: null,
              status: 'error',
              batchIndex,
            },
            fileUploadInfo,
          );
          return;
        }

        // 上传成功
        fileUploadInfo.is_success = true;
        fileUploadInfo.upload_time = performance.now() - startUploadTime;

        const video: SourceVideo = {
          id: videoUploadResult.id,
          tempId: id,
          src: videoUploadResult.uri,
          preview: videoURL,
          aspectRatio,
          duration,
          cover_url: coverUploadResult?.uri, // 封面图上传失败不影响生成视频
          cover_preview_url: cover_url,
          width,
          height,
        };

        onChange?.(
          {
            id,
            file: video,
            status: 'done',
            batchIndex,
            width,
            height,
          },
          fileUploadInfo,
        );
      } catch (error) {
        // 处理其他未预期的错误（比如 getVideoMetadata 获取视频元数据失败）
        let errorMsg = t('popup.toast.uploadingError');
        let failReason = 'uploadingError';

        const errorCode = getVideoMetadataErrorCode(error);
        if (errorCode) {
          // 错误码到 i18n key 和 failReason 的映射
          switch (errorCode) {
            case VideoMetadataErrorCode.METADATA_DIMENSIONS_FAILED:
              errorMsg = t('sharedComponent.videoUploader.error.metadataFailed');
              failReason = 'metadataFailed';
              break;
            case VideoMetadataErrorCode.METADATA_TIMEOUT:
              errorMsg = t('sharedComponent.videoUploader.error.metadataTimeout');
              failReason = 'metadataTimeout';
              break;
            case VideoMetadataErrorCode.METADATA_LOAD_FAILED:
              errorMsg = t('sharedComponent.videoUploader.error.metadataLoadFailed');
              failReason = 'metadataLoadFailed';
              break;
            case VideoMetadataErrorCode.METADATA_FAILED:
              errorMsg = t('sharedComponent.videoUploader.error.metadataFailed');
              failReason = 'metadataFailed';
              break;
            default:
              // 保持默认值
              break;
          }
        }

        fileUploadInfo.fail_reason = failReason;
        showError(errorMsg);
        onChange?.(
          {
            id,
            file: null,
            status: 'error',
            batchIndex,
          },
          fileUploadInfo,
        );
      } finally {
        setUploading(false);
        // 确保清理 objectUrl（如果之前创建了但未清理）
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }
    },
    [
      maxDuration,
      minDuration,
      maxSize,
      onChange,
      scene,
      captureCoverImage,
      uploadCoverImage,
      t,
      userInfo,
      allowTempMode,
      validateMetadata,
      maxSizeLabel,
      onMetadataReady,
      emitUploadingInTempMode,
      emitUploadingPreview,
    ],
  );

  /**
   * 触发文件选择器
   */
  const selectVideo = useCallback(() => {
    // 如果已经存在 input 元素，移除它
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
    }

    // 创建新的 input 元素
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = videoAccept.join(',');
    input.style.display = 'none';

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        await handleFileChange(file);
      }
      // 清理 input 元素
      if (fileInputRef.current) {
        document.body.removeChild(fileInputRef.current);
        fileInputRef.current = null;
      }
    };

    document.body.appendChild(input);
    fileInputRef.current = input;
    input.click();
  }, [handleFileChange]);

  /**
   * 取消上传
   */
  const cancelUpload = useCallback(() => {
    if (cancelUploadRef.current) {
      cancelUploadRef.current();
    }
  }, []);

  return {
    uploading,
    selectVideo,
    uploadFile: handleFileChange,
    cancelUpload,
  };
};
